import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { MessageCircle, X, Send, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GlowChat = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', text: "Hey love 💕 I'm your Glow assistant. Ask me anything about services, prices, or booking!" },
  ]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(() => {
    const existing = sessionStorage.getItem('glowChatSession');
    if (existing) return existing;
    const id = 'glow-' + Math.random().toString(36).slice(2, 12);
    sessionStorage.setItem('glowChatSession', id);
    return id;
  });
  const endRef = useRef(null);

  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    setMessages(m => [...m, { role: 'user', text }]);
    setSending(true);
    try {
      const r = await axios.post(`${API_URL}/api/gemini-chat`, { session_id: sessionId, message: text });
      setMessages(m => [...m, { role: 'assistant', text: r.data.reply }]);
    } catch (err) {
      setMessages(m => [...m, {
        role: 'assistant',
        text: "Hmm, I'm having trouble right now. Please text Christina directly at 904.672.9966 — she'll respond ASAP! 💕",
      }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating trigger button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="glow-chat-open"
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-5 py-3.5 rounded-full bg-gradient-to-r from-pink-300 to-pink-400 text-black font-medium shadow-2xl shadow-pink-500/40 hover:scale-105 transition-transform"
          aria-label="Open Glow assistant"
        >
          <Sparkles className="w-5 h-5" />
          Ask Glow AI
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className="fixed bottom-6 right-6 z-50 w-[92vw] max-w-sm h-[70vh] max-h-[600px] rounded-2xl border border-amber-700/40 bg-[#0a0405] shadow-2xl shadow-pink-500/20 flex flex-col overflow-hidden"
          data-testid="glow-chat-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-amber-900/30 bg-gradient-to-r from-amber-950/40 to-black/80">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-pink-300 to-pink-400 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-black" />
              </div>
              <div>
                <div className="text-amber-100 font-serif text-lg leading-tight">Glow AI</div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-amber-300/70">Available 24/7</div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-amber-100/60 hover:text-amber-100 p-1"
              data-testid="glow-chat-close"
              aria-label="Close chat"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                data-testid={`chat-msg-${m.role}-${i}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === 'user'
                      ? 'bg-gradient-to-r from-pink-300 to-pink-400 text-black rounded-br-sm'
                      : 'bg-amber-950/40 text-amber-100 border border-amber-700/30 rounded-bl-sm'
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start" data-testid="chat-typing">
                <div className="bg-amber-950/40 text-amber-100/60 border border-amber-700/30 rounded-2xl rounded-bl-sm px-4 py-2.5 text-sm">
                  <span className="inline-block animate-pulse">Glow is typing...</span>
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {/* Input */}
          <form
            onSubmit={send}
            className="border-t border-amber-900/30 p-3 flex gap-2 bg-black/60"
            data-testid="glow-chat-form"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about prices, services, booking..."
              disabled={sending}
              className="flex-1 bg-amber-950/30 border border-amber-700/40 rounded-full px-4 py-2.5 text-sm text-amber-100 placeholder:text-amber-100/40 focus:outline-none focus:border-pink-400"
              data-testid="glow-chat-input"
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              className="w-11 h-11 rounded-full bg-pink-300 text-black flex items-center justify-center hover:bg-pink-200 transition-colors disabled:opacity-40"
              data-testid="glow-chat-send"
              aria-label="Send message"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default GlowChat;
